import { z } from "zod";

/**
 * Closed enum of value kinds carried by a `field_values` row.
 *
 * Each kind maps to exactly one (or for `currency`, exactly two) of the
 * typed projection columns on `field_values`:
 *
 *   string   -> value_text
 *   enum     -> value_text
 *   number   -> value_numeric
 *   currency -> value_numeric + value_currency_code
 *   date     -> value_date
 *   datetime -> value_datetime
 *   boolean  -> value_bool
 *
 * The set is closed at the database level (CHECK constraint on
 * `field_definitions.value_type`) and at the Zod surface
 * (`ProjectedFieldValueSchema`). Adding a new kind requires a migration
 * AND a new entry in this constant — the helpers in this module are
 * total over `ValueKind`, so an unknown kind throws rather than
 * silently passing through.
 */
export const VALUE_KINDS = [
  "string",
  "enum",
  "number",
  "currency",
  "date",
  "datetime",
  "boolean",
] as const;

export type ValueKind = (typeof VALUE_KINDS)[number];

// ---------------------------------------------------------------------------
// Discriminated union of projected (in-memory, typed) field values.
//
// This is the shape used by the writer (M3) and the read helper to talk
// about a value's typed projection without touching the storage row's
// six-column shape directly. The discriminator (`valueKind`) is a member
// of `VALUE_KINDS`; the only extra fields are the typed payload columns
// that the kind requires.
//
// Numeric notes:
//   - `number` accepts any finite JS number. Storage is `numeric(30, 6)`,
//     and `projectedColumnsFromValue` serialises through `Number.toString()`
//     which Postgres will accept and round-trip without precision loss
//     for the magnitudes this column carries.
//   - `currency` constrains the amount to an integer (minor units, e.g.
//     cents) plus an ISO 4217 currency code. There is NO implicit
//     workspace-default currency; every currency row carries its own code.
//
// Date / datetime notes:
//   - `date` is a calendar date with no time component, serialised as
//     `YYYY-MM-DD`. Maps to Postgres `date`.
//   - `datetime` is an instant with timezone, serialised as RFC 3339 /
//     ISO 8601 with offset. Maps to Postgres `timestamptz`.
// ---------------------------------------------------------------------------

const StringValueSchema = z.object({
  valueKind: z.literal("string"),
  valueText: z.string(),
});

const EnumValueSchema = z.object({
  valueKind: z.literal("enum"),
  valueText: z.string(),
});

const NumberValueSchema = z.object({
  valueKind: z.literal("number"),
  valueNumeric: z.number(),
});

const CurrencyValueSchema = z.object({
  valueKind: z.literal("currency"),
  // Integer minor units. Currency precision questions are deferred to the
  // future provenance/decisions layer; v1 callers operate in minor units
  // (cents, etc.) so this is safe.
  valueNumeric: z.number().int(),
  // ISO 4217 alpha-3 code, e.g. "USD", "EUR", "GBP". Length-checked here;
  // membership in the actual ISO 4217 set is intentionally not validated
  // at the Zod boundary (we don't ship a currency-code table in v1).
  valueCurrencyCode: z.string().length(3),
});

const DateValueSchema = z.object({
  valueKind: z.literal("date"),
  // YYYY-MM-DD, no time component, no timezone. Drizzle's `date()` in
  // string mode reads/writes this same shape directly.
  valueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

const DatetimeValueSchema = z.object({
  valueKind: z.literal("datetime"),
  // ISO 8601 / RFC 3339 with explicit offset (e.g. `Z` or `+01:00`).
  // Forbids naked floating times, which would silently lose tz info on
  // round-trip through `timestamptz`.
  valueDatetime: z.string().datetime({ offset: true }),
});

const BooleanValueSchema = z.object({
  valueKind: z.literal("boolean"),
  valueBool: z.boolean(),
});

export const ProjectedFieldValueSchema = z.discriminatedUnion("valueKind", [
  StringValueSchema,
  EnumValueSchema,
  NumberValueSchema,
  CurrencyValueSchema,
  DateValueSchema,
  DatetimeValueSchema,
  BooleanValueSchema,
]);

export type ProjectedFieldValue = z.infer<typeof ProjectedFieldValueSchema>;

// ---------------------------------------------------------------------------
// Storage projection: in-memory <-> Postgres column shape.
//
// The storage row carries six typed columns plus `valueKind`. Exactly one
// (or for `currency`, exactly two) is non-null on any given row. These
// helpers are the only place the in-memory `ProjectedFieldValue` shape
// crosses the storage column boundary.
// ---------------------------------------------------------------------------

/**
 * Shape of the six typed-column outputs produced by `projectedColumnsFromValue`.
 *
 * - `valueNumeric` is a string in Postgres-compatible numeric format
 *   because Drizzle reads/writes `numeric(30, 6)` as a string. The
 *   round-trip through `valueFromProjectedColumns` parses it back into
 *   a JS number.
 * - `valueDate` is a `YYYY-MM-DD` string (Drizzle `date()` string mode).
 * - `valueDatetime` is an ISO 8601 / RFC 3339 string with offset.
 *   Drizzle `timestamp({ withTimezone: true })` can also accept `Date`
 *   on insert; we use the string form so the helper is purely string-in /
 *   string-out and easy to reason about at boundaries.
 */
export interface ProjectedColumns {
  valueText: string | null;
  valueNumeric: string | null;
  valueCurrencyCode: string | null;
  valueDate: string | null;
  valueDatetime: string | null;
  valueBool: boolean | null;
}

/**
 * Storage row shape consumed by `valueFromProjectedColumns`. The Drizzle
 * select type for `field_values` matches this superset (extra columns
 * such as `id`, `workspaceId`, etc. are ignored). `valueNumeric` is a
 * string at the storage boundary because that's what Drizzle hands back
 * for `numeric(30, 6)`. `valueDatetime` is accepted as either a string
 * (when callers stringify ahead of time) or a `Date` (Drizzle's default
 * for `timestamp({ withTimezone: true })`); both forms round-trip
 * through the same RFC 3339 surface.
 */
export interface FieldValueProjectionRow {
  valueKind: string;
  valueText: string | null;
  valueNumeric: string | null;
  valueCurrencyCode: string | null;
  valueDate: string | null;
  valueDatetime: string | Date | null;
  valueBool: boolean | null;
}

const EMPTY_COLUMNS: ProjectedColumns = {
  valueText: null,
  valueNumeric: null,
  valueCurrencyCode: null,
  valueDate: null,
  valueDatetime: null,
  valueBool: null,
};

/**
 * Convert an in-memory `ProjectedFieldValue` to the six-column storage
 * shape. Exactly one column is set for non-currency kinds; currency
 * sets `valueNumeric` AND `valueCurrencyCode` together. All other
 * columns are `null`.
 *
 * Numeric serialisation goes through `Number.prototype.toString()`,
 * which Postgres accepts as input to a `numeric(30, 6)` column and
 * round-trips losslessly for the magnitudes this column carries.
 *
 * The function is total over `ValueKind`. An input with a `valueKind`
 * outside the closed enum throws — there is no silent
 * "unsupported kind" branch.
 */
export function projectedColumnsFromValue(input: ProjectedFieldValue): ProjectedColumns {
  switch (input.valueKind) {
    case "string":
    case "enum":
      return { ...EMPTY_COLUMNS, valueText: input.valueText };
    case "number":
      return { ...EMPTY_COLUMNS, valueNumeric: numberToNumericString(input.valueNumeric) };
    case "currency":
      return {
        ...EMPTY_COLUMNS,
        valueNumeric: numberToNumericString(input.valueNumeric),
        valueCurrencyCode: input.valueCurrencyCode,
      };
    case "date":
      return { ...EMPTY_COLUMNS, valueDate: input.valueDate };
    case "datetime":
      return { ...EMPTY_COLUMNS, valueDatetime: input.valueDatetime };
    case "boolean":
      return { ...EMPTY_COLUMNS, valueBool: input.valueBool };
    default: {
      // Exhaustiveness check: if a new ValueKind is added without a
      // matching case, this line is a type error. At runtime, an input
      // with an unrecognised valueKind also throws.
      const exhaustive: never = input;
      throw new Error(
        `projectedColumnsFromValue: unrecognised valueKind ${JSON.stringify(
          (exhaustive as { valueKind?: unknown } | undefined)?.valueKind,
        )}`,
      );
    }
  }
}

/**
 * Reverse of `projectedColumnsFromValue`. Reads a storage row and
 * returns the typed in-memory value.
 *
 * Throws when:
 *   - `valueKind` is not a member of `VALUE_KINDS`.
 *   - The expected typed column for the kind is null.
 *   - More than the expected number of typed columns are populated
 *     (i.e. the writer's invariant has been violated). For currency
 *     this means both `valueNumeric` and `valueCurrencyCode` must be
 *     populated and no other typed column may be; for every other
 *     kind, exactly one typed column may be populated.
 */
export function valueFromProjectedColumns(row: FieldValueProjectionRow): ProjectedFieldValue {
  if (!isValueKind(row.valueKind)) {
    throw new Error(
      `valueFromProjectedColumns: unrecognised valueKind ${JSON.stringify(row.valueKind)}`,
    );
  }
  const kind: ValueKind = row.valueKind;
  const populated = populatedColumns(row);
  switch (kind) {
    case "string":
    case "enum": {
      assertExactlyOne(populated, "valueText", kind);
      return { valueKind: kind, valueText: row.valueText! };
    }
    case "number": {
      assertExactlyOne(populated, "valueNumeric", kind);
      return { valueKind: kind, valueNumeric: numericStringToNumber(row.valueNumeric!) };
    }
    case "currency": {
      assertExactlyTwo(populated, "valueNumeric", "valueCurrencyCode", kind);
      const amount = numericStringToNumber(row.valueNumeric!);
      if (!Number.isInteger(amount)) {
        throw new Error(
          `valueFromProjectedColumns: currency amount must be an integer, got ${row.valueNumeric}`,
        );
      }
      return {
        valueKind: kind,
        valueNumeric: amount,
        valueCurrencyCode: row.valueCurrencyCode!,
      };
    }
    case "date": {
      assertExactlyOne(populated, "valueDate", kind);
      return { valueKind: kind, valueDate: row.valueDate! };
    }
    case "datetime": {
      assertExactlyOne(populated, "valueDatetime", kind);
      const dt = row.valueDatetime!;
      const iso = dt instanceof Date ? dt.toISOString() : dt;
      return { valueKind: kind, valueDatetime: iso };
    }
    case "boolean": {
      assertExactlyOne(populated, "valueBool", kind);
      return { valueKind: kind, valueBool: row.valueBool! };
    }
    default: {
      const exhaustive: never = kind;
      throw new Error(`valueFromProjectedColumns: unhandled kind ${String(exhaustive)}`);
    }
  }
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

type TypedColumnName =
  | "valueText"
  | "valueNumeric"
  | "valueCurrencyCode"
  | "valueDate"
  | "valueDatetime"
  | "valueBool";

const TYPED_COLUMN_NAMES: readonly TypedColumnName[] = [
  "valueText",
  "valueNumeric",
  "valueCurrencyCode",
  "valueDate",
  "valueDatetime",
  "valueBool",
];

function isValueKind(input: unknown): input is ValueKind {
  return typeof input === "string" && (VALUE_KINDS as readonly string[]).includes(input);
}

function populatedColumns(row: FieldValueProjectionRow): TypedColumnName[] {
  const out: TypedColumnName[] = [];
  for (const name of TYPED_COLUMN_NAMES) {
    if (row[name] !== null && row[name] !== undefined) {
      out.push(name);
    }
  }
  return out;
}

function assertExactlyOne(
  populated: TypedColumnName[],
  expected: TypedColumnName,
  kind: ValueKind,
): void {
  if (populated.length !== 1 || populated[0] !== expected) {
    throw new Error(
      `valueFromProjectedColumns: kind=${kind} expects exactly [${expected}] non-null, got [${populated.join(", ")}]`,
    );
  }
}

function assertExactlyTwo(
  populated: TypedColumnName[],
  a: TypedColumnName,
  b: TypedColumnName,
  kind: ValueKind,
): void {
  if (populated.length !== 2 || !populated.includes(a) || !populated.includes(b)) {
    throw new Error(
      `valueFromProjectedColumns: kind=${kind} expects exactly [${a}, ${b}] non-null, got [${populated.join(", ")}]`,
    );
  }
}

function numberToNumericString(n: number): string {
  if (!Number.isFinite(n)) {
    throw new Error(`projectedColumnsFromValue: numeric value must be finite, got ${n}`);
  }
  return n.toString();
}

function numericStringToNumber(s: string): number {
  const parsed = Number(s);
  if (!Number.isFinite(parsed)) {
    throw new Error(
      `valueFromProjectedColumns: failed to parse numeric column ${JSON.stringify(s)} as a finite number`,
    );
  }
  return parsed;
}

// ---------------------------------------------------------------------------
// Runtime-value -> ProjectedFieldValue projection.
//
// Callers in the extractor / enricher / lab-fixture pipelines hold a runtime
// value (`unknown`, post-normalization) plus a `field_definitions.value_type`
// string. To go through `applyExtractedFieldValue`, they need a
// `ProjectedFieldValue`. This helper is the single boundary that performs
// that mapping. It is intentionally lenient on the in-memory input shape
// (e.g. accepts `Date` for date / datetime kinds, accepts numeric strings
// for `number`, accepts `0`/`1`/`"true"`/`"false"` for `boolean`) but
// strict on the output: any value that cannot be represented in the
// requested kind throws `FieldValueProjectionError`. This matches the
// "fail loudly" rule from the canonical-record plan.
// ---------------------------------------------------------------------------

/**
 * Thrown when `projectFieldValue` cannot map a runtime value to the
 * requested `ValueKind`. The `code` lets callers distinguish between
 * "value_type wasn't a closed enum member" and "the value did not parse
 * as the requested kind", which they may handle differently (e.g. log
 * vs. surface to the user).
 */
export class FieldValueProjectionError extends Error {
  readonly code: "VALUE_TYPE_UNKNOWN" | "VALUE_KIND_INVALID" | "CURRENCY_SHAPE_INVALID";

  constructor(
    code: FieldValueProjectionError["code"],
    message: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "FieldValueProjectionError";
    this.code = code;
  }
}

/**
 * Currency input shape accepted by `projectFieldValue`. The amount is in
 * minor units (integer), the code is a 3-letter ISO 4217 alpha code.
 * Currency values must always carry both fields explicitly — there is no
 * implicit workspace-default currency.
 */
export interface CurrencyInput {
  amount: number;
  currency_code: string;
}

/**
 * Project a runtime value (post-normalization) into a `ProjectedFieldValue`
 * suitable for `applyExtractedFieldValue` and the storage-projection
 * helpers. Returns `null` for nullish input; throws on type mismatches.
 *
 * The `valueType` argument MUST be a member of `VALUE_KINDS` (the closed
 * enum). The migration tightened `field_definitions.value_type` to that
 * set, so any caller building a `value_type` outside it should be fixed
 * upstream rather than papered over here.
 */
export function projectFieldValue(
  rawValue: unknown,
  valueType: string,
): ProjectedFieldValue | null {
  if (rawValue === null || rawValue === undefined) return null;
  if (!isValueKind(valueType)) {
    throw new FieldValueProjectionError(
      "VALUE_TYPE_UNKNOWN",
      `value_type ${JSON.stringify(valueType)} is not a member of the closed enum (${VALUE_KINDS.join(", ")})`,
      { valueType },
    );
  }
  switch (valueType) {
    case "string":
    case "enum": {
      const text = projectToString(rawValue);
      if (text === null) return null;
      return { valueKind: valueType, valueText: text };
    }
    case "number": {
      const n = projectToFiniteNumber(rawValue, valueType);
      return { valueKind: "number", valueNumeric: n };
    }
    case "currency": {
      const { amount, currencyCode } = projectToCurrency(rawValue);
      return {
        valueKind: "currency",
        valueNumeric: amount,
        valueCurrencyCode: currencyCode,
      };
    }
    case "date": {
      const iso = projectToDateString(rawValue);
      return { valueKind: "date", valueDate: iso };
    }
    case "datetime": {
      const iso = projectToDatetimeString(rawValue);
      return { valueKind: "datetime", valueDatetime: iso };
    }
    case "boolean": {
      const b = projectToBoolean(rawValue);
      return { valueKind: "boolean", valueBool: b };
    }
    default: {
      const exhaustive: never = valueType;
      throw new Error(`projectFieldValue: unhandled valueType ${String(exhaustive)}`);
    }
  }
}

function projectToString(raw: unknown): string | null {
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof raw === "number" || typeof raw === "boolean") return String(raw);
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return raw.toISOString();
  }
  // For arrays / plain objects, JSON-stringify so the caller does not
  // silently drop structured payloads. The receiving side is `value_text`
  // — anything richer should have been modelled with a different kind.
  return JSON.stringify(raw);
}

function projectToFiniteNumber(raw: unknown, kind: ValueKind): number {
  if (typeof raw === "number") {
    if (!Number.isFinite(raw)) {
      throw new FieldValueProjectionError(
        "VALUE_KIND_INVALID",
        `${kind} value must be finite, got ${raw}`,
        { kind, raw },
      );
    }
    return raw;
  }
  if (typeof raw === "string") {
    const parsed = Number(raw.trim());
    if (!Number.isFinite(parsed)) {
      throw new FieldValueProjectionError(
        "VALUE_KIND_INVALID",
        `${kind} value ${JSON.stringify(raw)} did not parse as a finite number`,
        { kind, raw },
      );
    }
    return parsed;
  }
  if (typeof raw === "boolean") return raw ? 1 : 0;
  throw new FieldValueProjectionError(
    "VALUE_KIND_INVALID",
    `${kind} value of type ${typeof raw} cannot be projected`,
    { kind, raw },
  );
}

function projectToCurrency(raw: unknown): { amount: number; currencyCode: string } {
  if (raw === null || raw === undefined || typeof raw !== "object" || Array.isArray(raw)) {
    throw new FieldValueProjectionError(
      "CURRENCY_SHAPE_INVALID",
      `currency value must be an object with { amount, currency_code }, got ${JSON.stringify(raw)}`,
      { raw },
    );
  }
  const obj = raw as Record<string, unknown>;
  const amountRaw =
    "amount" in obj ? obj.amount : "valueNumeric" in obj ? obj.valueNumeric : undefined;
  const codeRaw =
    "currency_code" in obj
      ? obj.currency_code
      : "currencyCode" in obj
        ? obj.currencyCode
        : "valueCurrencyCode" in obj
          ? obj.valueCurrencyCode
          : undefined;
  if (
    typeof amountRaw !== "number" ||
    !Number.isFinite(amountRaw) ||
    !Number.isInteger(amountRaw)
  ) {
    throw new FieldValueProjectionError(
      "CURRENCY_SHAPE_INVALID",
      `currency.amount must be an integer (minor units), got ${JSON.stringify(amountRaw)}`,
      { amountRaw },
    );
  }
  if (typeof codeRaw !== "string" || codeRaw.length !== 3) {
    throw new FieldValueProjectionError(
      "CURRENCY_SHAPE_INVALID",
      `currency.currency_code must be a 3-letter ISO 4217 code, got ${JSON.stringify(codeRaw)}`,
      { codeRaw },
    );
  }
  return { amount: amountRaw, currencyCode: codeRaw };
}

function projectToDateString(raw: unknown): string {
  if (raw instanceof Date) {
    if (Number.isNaN(raw.getTime())) {
      throw new FieldValueProjectionError("VALUE_KIND_INVALID", `date value Date(NaN) is invalid`, {
        raw,
      });
    }
    return raw.toISOString().slice(0, 10);
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    // YYYY-MM-DD canonical.
    const dateOnly = /^\d{4}-\d{2}-\d{2}$/u.exec(trimmed);
    if (dateOnly) return trimmed;
    // ISO 8601 date-time prefix.
    const datetimeMatch = /^(\d{4}-\d{2}-\d{2})T/u.exec(trimmed);
    if (datetimeMatch) return datetimeMatch[1]!;
    // US-style M/D/YYYY or MM/DD/YYYY. Insurance documents almost
    // exclusively use this form, and LLMs frequently echo it verbatim
    // from the source. Accept and convert; reject impossible values
    // (month > 12 or day > 31) to surface real garbage.
    const usSlash = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/u.exec(trimmed);
    if (usSlash) {
      const month = Number(usSlash[1]);
      const day = Number(usSlash[2]);
      const year = usSlash[3]!;
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      }
    }
    throw new FieldValueProjectionError(
      "VALUE_KIND_INVALID",
      `date value ${JSON.stringify(raw)} is not in YYYY-MM-DD, ISO 8601, or M/D/YYYY form`,
      { raw },
    );
  }
  throw new FieldValueProjectionError(
    "VALUE_KIND_INVALID",
    `date value of type ${typeof raw} cannot be projected`,
    { raw },
  );
}

function projectToDatetimeString(raw: unknown): string {
  if (raw instanceof Date) {
    if (Number.isNaN(raw.getTime())) {
      throw new FieldValueProjectionError(
        "VALUE_KIND_INVALID",
        `datetime value Date(NaN) is invalid`,
        { raw },
      );
    }
    return raw.toISOString();
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
      throw new FieldValueProjectionError(
        "VALUE_KIND_INVALID",
        `datetime value ${JSON.stringify(raw)} did not parse`,
        { raw },
      );
    }
    return parsed.toISOString();
  }
  throw new FieldValueProjectionError(
    "VALUE_KIND_INVALID",
    `datetime value of type ${typeof raw} cannot be projected`,
    { raw },
  );
}

function projectToBoolean(raw: unknown): boolean {
  if (typeof raw === "boolean") return raw;
  if (typeof raw === "number") {
    if (raw === 1) return true;
    if (raw === 0) return false;
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim().toLowerCase();
    if (["true", "yes", "y", "1"].includes(trimmed)) return true;
    if (["false", "no", "n", "0"].includes(trimmed)) return false;
  }
  throw new FieldValueProjectionError(
    "VALUE_KIND_INVALID",
    `boolean value ${JSON.stringify(raw)} cannot be projected`,
    { raw },
  );
}
